import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Globe, Shield, TrendingUp, Heart, Target, Eye, Menu, X, ChevronDown, ArrowRight, Rocket, Code, DollarSign, Lock, Smartphone } from 'lucide-react';
import SignupModal from '../SignupModal';
import LoginModal from '../LoginModal';
import ActivateCardModal from '../ActivateCardModal';

const AboutUsDetailedPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [signupStep, setSignupStep] = useState('details');
  const [loginStep, setLoginStep] = useState('email');
  const [signupData, setSignupData] = useState({ firstName:'', lastName:'', email:'', phone:'', country:'US', code:'', generatedCode:'' });
  const [loginData, setLoginData] = useState({ email:'', phone:'', loginMethod:'email', code:'', generatedCode:'' });
  const [activateData, setActivateData] = useState({ cardNumber:'', pin:'', code:'', generatedCode:'' });

  const countries = [
    { code:'US', name:'United States', requiresSSN:true },
    { code:'CA', name:'Canada', requiresSSN:false },
    { code:'UK', name:'United Kingdom', requiresSSN:false },
    { code:'DE', name:'Germany', requiresSSN:false },
    { code:'FR', name:'France', requiresSSN:false },
    { code:'NG', name:'Nigeria', requiresSSN:false },
    { code:'GH', name:'Ghana', requiresSSN:false },
    { code:'KE', name:'Kenya', requiresSSN:false },
    { code:'ZA', name:'South Africa', requiresSSN:false },
    { code:'BR', name:'Brazil', requiresSSN:false },
    { code:'IN', name:'India', requiresSSN:false },
  ];

  const menuItems = {
    Services: [
      { name:'Send Money', path:'/send-money' },
      { name:'Crypto Cards', path:'/crypto-cards' },
      { name:'Digital Wallets', path:'/digital-wallets' },
      { name:'Business Solutions', path:'/business-solutions' },
    ],
    Company: [
      { name:'About Us', path:'/about-us' },
      { name:'Careers', path:'/careers' },
      { name:'Press', path:'/press' },
      { name:'Investors', path:'/investors' },
    ],
    Support: [
      { name:'Help Center', path:'/help-center' },
      { name:'Contact Us', path:'/contact-us' },
      { name:'API Documentation', path:'/api-documentation' },
      { name:'Security', path:'/security' },
    ],
    Legal: [
      { name:'Terms of Service', path:'/terms-of-service' },
      { name:'Privacy Policy', path:'/privacy-policy' },
      { name:'Compliance', path:'/compliance' },
      { name:'Licenses', path:'/licenses' },
    ],
  };

  const toggleSubmenu = (menu) => setExpandedMenu(expandedMenu === menu ? null : menu);
  const generateAuthCode = () => Math.floor(100000 + Math.random() * 900000).toString();
  const setShowLandingPage = (value) => console.log('Navigation:', value);

  const openRoles = [
    {
      avatar: '👩‍💼',
      role: 'Chief Operating Officer',
      focus: 'Operations & Strategy',
      description: 'An operator who can translate vision into execution — building processes, managing partnerships and ensuring every part of the business runs smoothly at scale.',
      skills: ['Operations', 'Strategy', 'Business Development', 'Fintech'],
      type: 'Key Hire',
      typeColor: 'text-blue-300 bg-blue-500/10 border-blue-500/20',
    },
    {
      avatar: '👩‍⚖️',
      role: 'Chief Compliance Officer',
      focus: 'Regulatory & Legal',
      description: 'A compliance leader who understands MSB licensing, AML/KYC frameworks and can navigate international financial regulations across 180+ markets.',
      skills: ['MSB Licensing', 'AML / KYC', 'International Regulation', 'Banking Law'],
      type: 'Key Hire',
      typeColor: 'text-purple-300 bg-purple-500/10 border-purple-500/20',
    },
    {
      avatar: '👨‍💼',
      role: 'Chief Financial Officer',
      focus: 'Finance & Capital',
      description: 'A finance leader with fintech experience who can manage treasury, lead fundraising rounds, own unit economics and build the financial infrastructure for global scale.',
      skills: ['Fintech Finance', 'Treasury', 'Fundraising', 'Unit Economics'],
      type: 'Key Hire',
      typeColor: 'text-green-300 bg-green-500/10 border-green-500/20',
    },
    {
      avatar: '👩‍🎓',
      role: 'Head of Global Expansion',
      focus: 'Emerging Markets & Growth',
      description: 'Someone who understands how money moves in Africa, Latin America and Southeast Asia — opening corridors, building partnerships and winning the markets that matter most.',
      skills: ['Emerging Markets', 'Partnership Development', 'Market Entry', 'Remittance Corridors'],
      type: 'Key Hire',
      typeColor: 'text-orange-300 bg-orange-500/10 border-orange-500/20',
    },
    {
      avatar: '👨‍🔧',
      role: 'Chief Security Officer',
      focus: 'Cybersecurity & Fraud',
      description: 'A security expert who can harden a financial platform handling real money — from smart contract audits and encryption to fraud detection and incident response.',
      skills: ['Smart Contract Auditing', 'Cryptography', 'Fraud Detection', 'Pen Testing'],
      type: 'Key Hire',
      typeColor: 'text-red-300 bg-red-500/10 border-red-500/20',
    },
    {
      avatar: '👨‍💻',
      role: 'Senior Blockchain Engineer',
      focus: 'Protocol & Infrastructure',
      description: 'A blockchain specialist to co-own the protocol layer — smart contract architecture, cross-chain bridges, gas optimization and the settlement rails that make us 10x faster than SWIFT.',
      skills: ['Solidity / EVM', 'Node.js / Go', 'Cross-chain Bridges', 'DeFi Protocols'],
      type: 'Engineering',
      typeColor: 'text-yellow-300 bg-yellow-500/10 border-yellow-500/20',
    },
  ];

  const milestones = [
    { year: '2025', label: 'Q1', title: 'Idea & Research', desc: 'Identified the gap — no single platform combined crypto gift cards, enterprise batch payments and blockchain remittance. Research and product architecture began.' },
    { year: '2025', label: 'Q2', title: 'Platform Built', desc: 'Full-stack fintech web and mobile app designed and built from scratch by the founding team. Stripe, Plaid and blockchain payment rails integrated.' },
    { year: '2025', label: 'Q3', title: 'Product Live', desc: 'Platform launched. Crypto gift card shop, batch payroll system, and international money transfer — all three products operational and accessible.' },
    { year: '2025', label: 'Q4', title: 'Compliance & Incorporation', desc: 'Formal company incorporation and regulatory compliance framework underway. MSB registration and licensing process initiated.' },
    { year: '2026', label: 'Now', title: 'Scaling & Team Building', desc: 'Actively fundraising and building the leadership team. Focused on customer acquisition, corridor partnerships and preparing for global expansion.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">

      {/* Header */}
      <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/zanors-logo-full.png" alt="Zanors Logo" className="h-16 md:h-20" style={{ backgroundColor:'transparent' }} />
            </Link>
            <div className="flex items-center space-x-3">
              <button onClick={() => setShowSignupModal(true)} className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">Sign Up</button>
              <button onClick={() => setShowLoginModal(true)} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">Login</button>
              <button onClick={() => setShowActivateCardModal(true)} className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">Activate Card</button>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 hover:bg-purple-700 rounded-lg transition-colors" aria-label="Menu">
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              {Object.entries(menuItems).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <button onClick={() => toggleSubmenu(category)} className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg transition-colors">
                    <span className="font-semibold text-lg">{category}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedMenu === category ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedMenu === category && (
                    <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                      {items.map((item) => (
                        <Link key={item.name} to={item.path}
                          className={`block p-3 hover:bg-purple-700 rounded-lg transition-colors ${item.path === '/about-us' ? 'text-white font-medium bg-purple-700' : 'text-gray-200 hover:text-white'}`}
                          onClick={() => setIsMenuOpen(false)}>
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* QR sidebar */}
      <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-40 cursor-pointer" onMouseEnter={() => setShowQRCode(true)} onMouseLeave={() => setShowQRCode(false)}>
        <div className={`transition-all duration-300 ${showQRCode ? 'w-48' : 'w-16'} bg-purple-800 border-l-4 border-green-400 rounded-l-lg shadow-lg`}>
          {!showQRCode ? (
            <div className="p-4"><div className="w-8 h-8 bg-white rounded grid grid-cols-2 gap-0.5 p-1"><div className="bg-black"></div><div className="bg-white"></div><div className="bg-white"></div><div className="bg-black"></div></div></div>
          ) : (
            <div className="p-4">
              <h3 className="text-sm font-bold mb-2 text-center">Get the ZANORS App</h3>
              <div className="bg-white p-2 rounded mb-2">
                <div className="grid grid-cols-8 gap-0.5">{[...Array(64)].map((_, i) => (<div key={i} className={`aspect-square ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}></div>))}</div>
              </div>
              <div className="space-y-2">
                <a href="https://apps.apple.com/app/zanors" className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors">App Store</a>
                <a href="https://play.google.com/store/apps/details?id=com.zanors.app" className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors">Google Play</a>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-6">

          {/* ── HERO ── */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 rounded-full px-5 py-2 text-sm font-semibold text-orange-300 mb-8">
              <Rocket className="w-4 h-4" />
              Founded 2025 · Early-Stage Fintech Startup
            </div>
            <h1 className="text-6xl md:text-7xl font-extrabold mb-6 leading-tight">
              We Are <span className="bg-gradient-to-r from-orange-400 to-yellow-300 bg-clip-text text-transparent">Zanors.</span>
            </h1>
            <p className="text-2xl text-purple-200 max-w-4xl mx-auto leading-relaxed mb-6">
              A fintech startup building the infrastructure to replace the world's most expensive,
              inaccessible and outdated financial services — through crypto gift cards available at
              200,000+ stores, enterprise batch payment automation, and blockchain-powered
              international money transfers.
            </p>
            <p className="text-lg text-purple-300 max-w-3xl mx-auto">
              We are a lean founding team who built the entire platform from scratch.
              We are now building the world-class team and partnerships to scale it globally.
            </p>
          </div>

          {/* ── MISSION / VISION / VALUES ── */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Purpose</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-900/60 to-purple-900/60 rounded-3xl p-8 border border-blue-500/40 text-center">
                <Target className="text-blue-400 w-12 h-12 mb-5 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-purple-200 leading-relaxed">
                  To democratize access to financial services through blockchain technology —
                  making secure, affordable and instant money movement available to everyone,
                  regardless of their banking status or where they live on earth.
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-900/60 to-blue-900/60 rounded-3xl p-8 border border-green-500/40 text-center">
                <Eye className="text-green-400 w-12 h-12 mb-5 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-purple-200 leading-relaxed">
                  A world where financial borders do not exist. Where sending money overseas
                  costs less than a text message. Where the 1.4 billion unbanked have the same
                  financial access as anyone in New York or London.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-900/60 to-pink-900/60 rounded-3xl p-8 border border-purple-500/40 text-center">
                <Heart className="text-purple-400 w-12 h-12 mb-5 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Our Values</h3>
                <p className="text-purple-200 leading-relaxed">
                  Radical transparency, uncompromising security and relentless inclusion.
                  We say what we mean, we build what we promise, and we treat every
                  user's money with the same care we would give our own.
                </p>
              </div>
            </div>
          </div>

          {/* ── FOUNDER ── */}
          <div className="mb-20">
            <div className="bg-gradient-to-r from-slate-900/80 via-indigo-900/60 to-slate-900/80 border border-white/10 rounded-3xl p-12">
              <div className="text-center mb-10">
                <h2 className="text-4xl font-bold mb-4">The Founding Team</h2>
                <p className="text-purple-300 text-lg max-w-2xl mx-auto">
                  Zanors was conceived, designed and built entirely by its founder —
                  a rare combination of product vision and full-stack technical execution.
                </p>
              </div>

              <div className="max-w-3xl mx-auto">
                <div className="bg-white/5 border border-white/15 rounded-3xl p-10">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-28 h-28 bg-gradient-to-br from-orange-500 to-purple-600 rounded-3xl flex items-center justify-center text-5xl shadow-xl">
                        👨‍💻
                      </div>
                      <div className="text-center mt-3">
                        <span className="text-xs bg-orange-500/20 border border-orange-500/30 text-orange-300 px-3 py-1 rounded-full font-semibold">Uchenna Odorh</span>
                      </div>
                    </div>
                    {/* Info */}
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-3xl font-extrabold mb-1">CEO & CTO</h3>
                      <p className="text-orange-400 font-semibold text-lg mb-4">Builder · Product Designer · Full-Stack Engineer</p>
                      <p className="text-purple-200 leading-relaxed mb-6">
                        The founder is both the business and technical mind behind Zanors — personally designing,
                        engineering and shipping the entire web and mobile application from the ground up.
                        From smart contract architecture to UI/UX design to payment rail integrations,
                        every layer of the product was built by one person with a clear and determined vision:
                        to give the world a better, cheaper, faster way to move money.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { icon: '🌐', label: 'Web App', sub: 'Built from scratch' },
                          { icon: '📱', label: 'Mobile App', sub: 'iOS & Android' },
                          { icon: '⛓️', label: 'Blockchain Rails', sub: 'Smart contracts' },
                          { icon: '💳', label: 'Payment Integration', sub: 'Stripe & Plaid' },
                        ].map(({ icon, label, sub }) => (
                          <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                            <div className="text-2xl mb-1">{icon}</div>
                            <div className="font-bold text-white text-sm">{label}</div>
                            <div className="text-purple-400 text-xs mt-0.5">{sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── KEY HIRES ── */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-green-500/15 border border-green-500/30 rounded-full px-5 py-2 text-sm font-semibold text-green-300 mb-6">
                <Users className="w-4 h-4" />
                We Are Hiring
              </div>
              <h2 className="text-4xl font-bold mb-4">Building the Dream Team</h2>
              <p className="text-purple-300 text-xl max-w-3xl mx-auto leading-relaxed">
                Great companies are built by great teams. The product is live, the vision is clear —
                these are the key people we are seeking to bring Zanors to global scale.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {openRoles.map((role, i) => (
                <div key={i} className="bg-purple-800/60 border border-purple-600/60 rounded-3xl p-7 flex flex-col hover:border-purple-400/60 transition-colors">
                  <div className="flex items-start justify-between mb-5">
                    <div className="text-5xl">{role.avatar}</div>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${role.typeColor}`}>
                      {role.type}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">{role.role}</h3>
                  <p className="text-purple-400 text-sm font-medium mb-4">{role.focus}</p>
                  <p className="text-purple-200 text-sm leading-relaxed mb-5 flex-1">{role.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {role.skills.map(s => (
                      <span key={s} className="text-xs bg-white/5 border border-white/10 text-purple-300 px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/careers" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-2xl text-lg transition-colors shadow-lg">
                See All Open Positions <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* ── TIMELINE ── */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Journey</h2>
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-purple-700/50"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={i} className="relative flex gap-8 items-start">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-purple-600 rounded-2xl flex flex-col items-center justify-center z-10 shadow-lg">
                      <span className="text-xs font-bold text-white/70">{m.year}</span>
                      <span className="text-sm font-black text-white">{m.label}</span>
                    </div>
                    <div className="bg-purple-900/40 border border-purple-700/50 rounded-2xl p-6 flex-1 mt-2">
                      <h3 className="text-lg font-bold text-white mb-2">{m.title}</h3>
                      <p className="text-purple-200 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── WHAT WE BUILT ── */}
          <div className="mb-20">
            <div className="bg-gradient-to-r from-purple-900/80 via-indigo-900/80 to-purple-900/80 border border-purple-700/50 rounded-3xl p-12">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">What We Have Already Built</h2>
                <p className="text-purple-300 text-lg max-w-2xl mx-auto">
                  We are not an idea on a slide deck. The platform is live, the integrations are real,
                  and every line of code was written by the founding team.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { stat:'3', label:'Distinct Products', desc:'Crypto gift cards, enterprise batch payments, and blockchain remittance — all in one unified platform.' },
                  { stat:'180+', label:'Countries Reachable', desc:'Transfer rails and currency support spanning 180+ countries from day one.' },
                  { stat:'200K+', label:'Retail Store Locations', desc:'BTC Gift Cards distributable through 200,000+ US retail locations via existing gift card networks.' },
                  { stat:'< 30s', label:'Settlement Time', desc:'Blockchain-native settlement. Not 3-5 business days. Real money moving in under 30 seconds.' },
                ].map(({ stat, label, desc }) => (
                  <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-7 text-center">
                    <div className="text-5xl font-extrabold text-orange-400 mb-2">{stat}</div>
                    <div className="text-lg font-bold text-white mb-3">{label}</div>
                    <p className="text-sm text-purple-300 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── CTA ── */}
          <div className="bg-gradient-to-r from-orange-600/20 via-purple-900/60 to-indigo-900/60 border border-orange-500/30 rounded-3xl p-12 text-center">
            <h2 className="text-4xl font-bold mb-4">Join the Mission</h2>
            <p className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              Whether you are a customer, investor, partner or future team member —
              there is a place for you in what we are building.
              Borderless money is not the future. It is what we are shipping today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={() => setShowSignupModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-2xl font-bold text-lg transition-colors shadow-lg">
                Create Free Account
              </button>
              <Link to="/investors" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-10 py-4 rounded-2xl font-bold text-lg transition-colors inline-flex items-center gap-2">
                Investor Relations <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black bg-opacity-50 mt-16 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {Object.entries(menuItems).map(([section, items]) => (
              <div key={section}>
                <h4 className="font-bold text-lg mb-4">{section}</h4>
                <ul className="space-y-2 text-purple-300">
                  {items.map(item => (
                    <li key={item.name}><Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">ZANORS is a financial technology platform. ZANORS is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services for the placement of deposits where the customers'</p>
                  <p className="text-xs text-purple-400">funds may be placed are provided by BankProv, Member FDIC. Deposit insurance coverage only</p>
                  <p className="text-xs text-purple-400">protects against the failure of an FDIC insured depository institution. Crypto assets and</p>
                  <p className="text-xs text-purple-400">non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-purple-300 text-sm">© 2026 Zanors Inc. All rights reserved.</p>
                <p className="text-purple-400 text-xs mt-1">Stripe Integrated · Plaid Connected · Blockchain-Powered</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <SignupModal showSignupModal={showSignupModal} setShowSignupModal={setShowSignupModal} signupStep={signupStep} setSignupStep={setSignupStep} signupData={signupData} setSignupData={setSignupData} countries={countries} generateAuthCode={generateAuthCode} setShowLandingPage={setShowLandingPage} />
      <LoginModal showLoginModal={showLoginModal} setShowLoginModal={setShowLoginModal} loginStep={loginStep} setLoginStep={setLoginStep} loginData={loginData} setLoginData={setLoginData} generateAuthCode={generateAuthCode} setShowLandingPage={setShowLandingPage} />
      <ActivateCardModal showActivateCardModal={showActivateCardModal} setShowActivateCardModal={setShowActivateCardModal} activateData={activateData} setActivateData={setActivateData} generateAuthCode={generateAuthCode} user={null} signupData={signupData} loginData={loginData} setShowLandingPage={setShowLandingPage} />
    </div>
  );
};

export default AboutUsDetailedPage;